import { CHART_LIMITS, REQUIRED_PALETTE_KEYS, type ChartAudio, type ChartPalette } from '@shared/chart';
import type { FlipPad } from '@shared/pulseChart';
import type { ManiaNote } from '@shared/maniaNote';

interface EditorDraftBase {
  title: string;
  description: string;
  slug: string;
  songSlug: string;
  audio: ChartAudio;
  bpm: number;
  totalBeats: number;
  difficulty: number;
  isPublic: boolean;
  palette: ChartPalette;
}

export interface PulseEditorDraft extends EditorDraftBase {
  mode: 'pulse';
  pads: FlipPad[];
}

export interface GuitarEditorDraft extends EditorDraftBase {
  mode: 'guitar';
  notes: ManiaNote[];
  approachMs: number;
}

export function editorDraftKey(uid: string, mode: 'pulse' | 'guitar', slug?: string): string {
  return `beatheaven-editor:${uid}:${mode}:${slug ? `map:${slug}` : 'new'}`;
}

function isEditorDraft(value: unknown): value is PulseEditorDraft | GuitarEditorDraft {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const draft = value as PulseEditorDraft | GuitarEditorDraft;
  if (![draft.title, draft.description, draft.slug, draft.songSlug].every((v) => typeof v === 'string') ||
    !Number.isFinite(draft.difficulty) || typeof draft.isPublic !== 'boolean' ||
    !draft.palette || !REQUIRED_PALETTE_KEYS.every((key) => typeof draft.palette[key] === 'string')) {
    return false;
  }
  const audio = draft.audio;
  if (!Number.isFinite(draft.bpm) || draft.bpm <= 0 || !Number.isFinite(draft.totalBeats) ||
    draft.totalBeats <= 0 || draft.totalBeats > CHART_LIMITS.maxTotalBeats ||
    !audio || typeof audio.src !== 'string' || typeof audio.attribution !== 'string' ||
    audio.bpm !== draft.bpm || !Number.isFinite(audio.offsetMs) || audio.offsetMs < 0 ||
    !Number.isFinite(audio.durationSec) || audio.durationSec <= 0 ||
    !Array.isArray(audio.onsets) || audio.onsets.length > CHART_LIMITS.maxOnsets ||
    !audio.onsets.every((onset) => onset && Number.isFinite(onset.beat) &&
      Number.isFinite(onset.intensity) && ['kick', 'snare', 'lead'].includes(onset.kind))) {
    return false;
  }
  if (draft.mode === 'pulse') {
    return Array.isArray(draft.pads) && draft.pads.every((p) => p && typeof p.id === 'string' &&
      Number.isFinite(p.beat) && (p.surface === 'floor' || p.surface === 'ceiling') &&
      (p.optional === undefined || typeof p.optional === 'boolean'));
  }
  return draft.mode === 'guitar' && Number.isFinite(draft.approachMs) &&
    Array.isArray(draft.notes) && draft.notes.every((n) => n && typeof n.id === 'string' &&
      Number.isFinite(n.beat) && Number.isInteger(n.lane) && n.lane >= 0 && n.lane < 4 &&
      (n.holdEndBeat === undefined || Number.isFinite(n.holdEndBeat)));
}

export function isPulseEditorDraft(value: unknown): value is PulseEditorDraft {
  return isEditorDraft(value) && value.mode === 'pulse';
}

export function isGuitarEditorDraft(value: unknown): value is GuitarEditorDraft {
  return isEditorDraft(value) && value.mode === 'guitar';
}
